import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import { supabase } from '../../lib/supabase';

const PartnerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partner, signIn, loading: authLoading } = usePartnerAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  
  // 🔥 CRITICAL FIX: Remove password reset handling from login page
  // Password reset should ONLY happen on /partner-password-reset page
  // This page should ONLY handle login functionality

  // Check for success message from password reset (when redirected back from reset page)
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      if (location.state?.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Standard redirect for authenticated partners
  useEffect(() => {
    if (!authLoading && partner) {
      console.log('✅ Partner authenticated - redirecting to dashboard');
      navigate('/partner-dashboard', { replace: true });
    }
  }, [partner, authLoading, navigate]);

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      const normalizedEmail = resetEmail.toLowerCase().trim();
      
      // First, verify if partner exists in the system
      const { data: partnerCheck, error: checkError } = await supabase
        .rpc('get_partner_by_email', { login_email: normalizedEmail });

      if (checkError || !partnerCheck || partnerCheck.length === 0) {
        setResetError('No partner account found with this email address. Please contact support.');
        setIsResetting(false);
        return;
      }

      const partner = partnerCheck[0];
      
      // Check if partner is active
      if (partner.status !== 'active') {
        setResetError('Your partner account is not active yet. Please contact support.');
        setIsResetting(false);
        return;
      }

      // Send password reset email with redirect to dedicated reset page
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: `${window.location.origin}/partner-password-reset`,
      });

      if (resetError) {
        // If Supabase auth fails, provide helpful error message
        if (resetError.message.includes('User not found') || resetError.message.includes('Unable to process request')) {
          setResetError(
            'Your partner account is not yet configured for password reset. Please contact support at support@travelheroes.at or use the demo credentials if you\'re testing.'
          );
        } else {
          setResetError(`Password reset failed: ${resetError.message}. Please contact support at support@travelheroes.at`);
        }
        setIsResetting(false);
        return;
      }

      setResetSuccess('Password reset email sent! Please check your inbox (and spam folder) for the reset link.');
      setResetEmail('');
      setTimeout(() => {
        setShowResetPassword(false);
        setResetSuccess(null);
      }, 5000);
      
    } catch (err) {
      console.error('Password reset error:', err);
      setResetError('An unexpected error occurred. Please contact support at support@travelheroes.at');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn(formData.email, formData.password);
      
      if (result.success) {
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/partner-dashboard');
        }, 1500);
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const fillDemoCredentials = (email: string) => {
    setFormData({
      email: email,
      password: 'Hallo1234'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show password reset request form
  if (showResetPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="KeyRound" size={32} className="text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Reset Password</h1>
            <p className="text-muted-foreground mt-2">
              Enter your partner email address and we'll send you a link to reset your password
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handlePasswordResetRequest}>
            {resetSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Email Sent!</p>
                  <p className="text-xs mt-1">{resetSuccess}</p>
                </div>
              </div>
            )}

            {resetError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Password Reset Failed</p>
                    <p className="text-xs mt-1">{resetError}</p>
                  </div>
                </div>
                {resetError.includes('contact support') && (
                  <div className="mt-3 pt-3 border-t border-red-200">
                    <p className="text-xs font-semibold mb-2">Need Help?</p>
                    <div className="flex items-center gap-2 text-xs">
                      <Icon name="Mail" size={14} className="text-red-600" />
                      <a 
                        href="mailto:admin@travelheroes.com" 
                        className="text-red-700 hover:text-red-900 underline"
                      >
                        admin@travelheroes.com
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-foreground mb-2">
                Partner Email
              </label>
              <Input
                id="reset-email"
                name="reset-email"
                type="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="your-business@example.com"
                disabled={isResetting}
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowResetPassword(false);
                  setResetEmail('');
                  setResetError(null);
                  setResetSuccess(null);
                }}
                disabled={isResetting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isResetting}
                iconName={isResetting ? "Loader2" : "Mail"}
                iconPosition="left"
              >
                {isResetting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-blue-900 font-medium mb-1">Testing with Demo Accounts?</p>
                  <p className="text-xs text-blue-800">
                    Demo partner accounts use password <code className="bg-blue-100 px-1 py-0.5 rounded">Hallo1234</code>. 
                    If you need to reset your real partner account password, please contact support.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Icon name="Building2" size={32} className="text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">TravelHeroes</h1>
          <p className="text-xl font-semibold text-primary mt-2">Partner Portal</p>
          <p className="text-muted-foreground mt-2">
            Sign in to manage your offers and track performance
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Success!</p>
                <p className="text-xs mt-1">{success}</p>
              </div>
            </div>
          )}

          {error && !success && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Login Failed</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Partner Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="your-business@example.com"
              disabled={isLoading || success !== null}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Password
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowResetPassword(true);
                  setResetEmail(formData.email);
                }}
                className="text-xs text-primary hover:text-primary/80 font-medium"
                disabled={isLoading || success !== null}
              >
                Forgot Password?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={isLoading || success !== null}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={isLoading || success !== null}
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || success !== null}
            iconName={isLoading || success ? "Loader2" : "LogIn"}
            iconPosition="left"
          >
            {success ? 'Redirecting...' : isLoading ? 'Signing In...' : 'Sign In to Partner Portal'}
          </Button>
        </form>

        {/* Enhanced Demo Credentials Section with password info */}
        <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-purple-900 flex items-center gap-2">
              <Icon name="Key" size={18} />
              ✨ Demo Credentials (For Testing)
            </h3>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
              LIVE
            </span>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between p-2 bg-white rounded hover:bg-purple-50 transition-colors group">
              <div className="flex-1">
                <span className="font-semibold text-purple-900 block">Vienna Coffee House</span>
                <code className="text-purple-600 text-xs">partner1@travelheroes.at</code>
              </div>
              <button
                type="button"
                onClick={() => fillDemoCredentials('partner1@travelheroes.at')}
                className="text-purple-600 hover:text-purple-800 font-medium text-xs px-3 py-1 rounded bg-purple-100 hover:bg-purple-200 transition-colors opacity-0 group-hover:opacity-100"
                disabled={isLoading || success !== null}
              >
                Use →
              </button>
            </div>
            <div className="flex items-center justify-between p-2 bg-white rounded hover:bg-purple-50 transition-colors group">
              <div className="flex-1">
                <span className="font-semibold text-purple-900 block">Schnitzel Palace</span>
                <code className="text-purple-600 text-xs">partner2@travelheroes.at</code>
              </div>
              <button
                type="button"
                onClick={() => fillDemoCredentials('partner2@travelheroes.at')}
                className="text-purple-600 hover:text-purple-800 font-medium text-xs px-3 py-1 rounded bg-purple-100 hover:bg-purple-200 transition-colors opacity-0 group-hover:opacity-100"
                disabled={isLoading || success !== null}
              >
                Use →
              </button>
            </div>
            <div className="flex items-center justify-between p-2 bg-white rounded hover:bg-purple-50 transition-colors group">
              <div className="flex-1">
                <span className="font-semibold text-purple-900 block">Prater Adventure</span>
                <code className="text-purple-600 text-xs">partner3@travelheroes.at</code>
              </div>
              <button
                type="button"
                onClick={() => fillDemoCredentials('partner3@travelheroes.at')}
                className="text-purple-600 hover:text-purple-800 font-medium text-xs px-3 py-1 rounded bg-purple-100 hover:bg-purple-200 transition-colors opacity-0 group-hover:opacity-100"
                disabled={isLoading || success !== null}
              >
                Use →
              </button>
            </div>
            <div className="flex items-center justify-between p-2 bg-white rounded hover:bg-purple-50 transition-colors group">
              <div className="flex-1">
                <span className="font-semibold text-purple-900 block">Stephansdom Tours</span>
                <code className="text-purple-600 text-xs">partner4@travelheroes.at</code>
              </div>
              <button
                type="button"
                onClick={() => fillDemoCredentials('partner4@travelheroes.at')}
                className="text-purple-600 hover:text-purple-800 font-medium text-xs px-3 py-1 rounded bg-purple-100 hover:bg-purple-200 transition-colors opacity-0 group-hover:opacity-100"
                disabled={isLoading || success !== null}
              >
                Use →
              </button>
            </div>
            <div className="flex items-center justify-between p-2 bg-white rounded hover:bg-purple-50 transition-colors group">
              <div className="flex-1">
                <span className="font-semibold text-purple-900 block">Belvedere Shop</span>
                <code className="text-purple-600 text-xs">partner5@travelheroes.at</code>
              </div>
              <button
                type="button"
                onClick={() => fillDemoCredentials('partner5@travelheroes.at')}
                className="text-purple-600 hover:text-purple-800 font-medium text-xs px-3 py-1 rounded bg-purple-100 hover:bg-purple-200 transition-colors opacity-0 group-hover:opacity-100"
                disabled={isLoading || success !== null}
              >
                Use →
              </button>
            </div>
            <div className="mt-3 p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Lock" size={14} className="text-purple-700" />
                <p className="text-xs font-bold text-purple-900">
                  Demo Password:
                </p>
              </div>
              <code className="text-sm font-bold text-purple-700 bg-white px-3 py-1 rounded border border-purple-300 inline-block">
                Hallo1234
              </code>
            </div>
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded flex items-start gap-2">
              <Icon name="AlertTriangle" size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-amber-800">
                  <strong>Can't Login?</strong> If you're a real partner created through the acquisition pipeline and don't know your password, click "Forgot Password?" above to reset it.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Need partner access?{' '}
            <Link to="/auth/login" className="text-primary hover:text-primary/80 font-medium">
              Contact Admin
            </Link>
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Are you an admin user?
            </p>
            <Button
              onClick={() => navigate('/auth/login')}
              variant="outline"
              className="w-full"
              iconName="Users"
              iconPosition="left"
            >
              Go to Admin Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLoginPage;